/**
 * `useLakeDoc()` — open a data-lake document in the `oem-source` drawer.
 *
 * The demo's `openDoc('x:<file>', true)`: a document index row names a **file**
 * (`'L-02_MXASSET.json'`), while the lake is addressed by **slug**
 * (`'13-maximo-…-l-02-mxasset-json'`), because file names are not unique across
 * the thirteen source folders. `lake/index.json` carries the `by_file` map that
 * bridges the two, and this hook is the one place that walk happens.
 *
 * Two fetches, both cached by `useOemData`'s module-level promise cache: the
 * 271-row index once per session, and the document itself once per document.
 * Nothing downloads the 2.0 MB lake.
 *
 * A file that is not in the index still opens the drawer — with the row's own
 * provenance and a line saying the body is not in the extracted pack. Silently
 * doing nothing on click would read as a broken control.
 *
 * Shared layer. Fork-owned (zone C), added by the P3c lane.
 *
 * @see src/versions/v1/chrome-oem/SourceDrawerBody.tsx — the payload contract
 * @see src/versions/v1/shared/useOemData.ts — `loadLakeIndex` / `loadLakeDoc`
 */

import { useCallback } from 'react';
import { useDrawer } from '@/app/providers';
import type { SourceDocPayload } from '../chrome-oem/SourceDrawerBody';
import { loadLakeDoc, loadLakeIndex } from './useOemData';

/** What the caller knows about a document before it is opened. */
export interface LakeDocRequest {
  /** File name as the index row spells it, e.g. `'L-02_MXASSET.json'`. */
  file: string;
  /** Source system, printed as the drawer's provenance. */
  connector: string;
  /** Title for the drawer header; defaults to the file name. */
  title?: string;
}

/**
 * Returns an opener. Calling it resolves the slug, fetches the document and
 * opens the drawer; failures open the drawer with the reason rather than
 * throwing into a click handler.
 */
export function useLakeDoc(): (request: LakeDocRequest) => void {
  const { open } = useDrawer();

  return useCallback(
    (request: LakeDocRequest) => {
      const show = (path: string, text: string) => {
        const payload: SourceDocPayload = {
          kind: 'doc',
          title: request.title ?? request.file,
          path,
          conn: request.connector,
          text,
        };
        // The drawer header owns the title — `SourceDrawerBody` no longer
        // prints it, so it has to travel on the drawer props. The kicker is
        // left at the `oem-source` default ("Source"); the body's own
        // "Document" eyebrow is what marks this as a lake file.
        open('oem-source', {
          title: payload.title,
          data: payload as unknown as Record<string, unknown>,
        });
      };

      loadLakeIndex()
        .then((index) => {
          const slug = index.by_file[request.file];
          if (!slug) {
            show(
              request.file,
              'This document is catalogued against the machine but its body was not included in the extracted pack.',
            );
            return;
          }
          return loadLakeDoc(slug).then((doc) => {
            show(doc.path, doc.text ?? describeWithoutText(doc.kind));
          });
        })
        .catch((cause: unknown) => {
          show(
            request.file,
            `The document could not be loaded: ${cause instanceof Error ? cause.message : String(cause)}`,
          );
        });
    },
    [open],
  );
}

/**
 * 20 of the 271 lake documents carry a table, worksheets or only a thumbnail
 * rather than extracted text. The drawer body renders text, so say what is
 * there instead of showing an empty panel.
 */
function describeWithoutText(kind: string): string {
  return `This ${kind.toUpperCase()} carries tabular data rather than extracted text. Open it in the data browser to see the grid.`;
}
